import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PartyPopper, MapPin, Clock, FileText, ArrowRight } from "lucide-react";

/**
 * Community event card for the Labor Day Block Party.
 *
 * Two modes:
 *   variant="public"  — used on the home page. Shows the event details and links
 *                       people to /portal (member RSVP flow). No user context.
 *   variant="portal"  — used on the portal dashboard. Same content, but the CTA
 *                       scrolls the page down to the inline <LaborDayRsvpForm />.
 *
 * The card auto-hides once the event date has passed so we don't display stale
 * banners in future years.
 */
export default function LaborDayEventCard({ variant = "public" }) {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/events/labor-day")
      .then((r) => { if (!cancelled) setEvent(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!event) return null;
  // Hide after the event has passed
  try {
    const eventDate = new Date(event.date + "T23:59:59");
    if (Date.now() > eventDate.getTime()) return null;
  } catch { /* ignore */ }

  const scrollToForm = () => {
    const el = document.getElementById("labor-day-rsvp-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Card
      data-testid="labor-day-event-card"
      className="rounded-2xl border-0 overflow-hidden mb-8 bg-gradient-to-br from-[#C86650] to-[#8A3E2F] text-white shadow-[0_20px_50px_rgba(200,102,80,0.35)]"
    >
      <CardContent className="p-8 md:p-10 relative">
        <div className="absolute -right-6 -top-6 opacity-10 pointer-events-none">
          <PartyPopper className="w-40 h-40" />
        </div>
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1 min-w-0">
            <p className="text-xs tracking-[0.3em] uppercase text-[#FBE7D7] mb-3">Community Event</p>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-3">{event.title}</h3>
            <p className="text-[#FBE7D7] max-w-xl leading-relaxed text-sm md:text-base mb-5">
              {event.description}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#FBE7D7]">
              <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" />{event.date_label} · {event.time_label}</span>
              <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" />{event.location}</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {variant === "portal" ? (
                <Button
                  data-testid="labor-day-rsvp-cta"
                  onClick={scrollToForm}
                  className="rounded-full bg-white text-[#C86650] hover:bg-[#FDF9F1] h-11 px-6"
                >
                  RSVP & Volunteer <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Link to="/portal">
                  <Button
                    data-testid="labor-day-rsvp-cta"
                    className="rounded-full bg-white text-[#C86650] hover:bg-[#FDF9F1] h-11 px-6"
                  >
                    RSVP in your Portal <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              )}
              {event.flyer_url && (
                <a href={event.flyer_url} target="_blank" rel="noreferrer" data-testid="labor-day-flyer-link">
                  <Button
                    variant="outline"
                    className="rounded-full border-white/70 text-white bg-white/10 hover:bg-white/20 hover:text-white h-11 px-6"
                  >
                    <FileText className="mr-2 w-4 h-4" /> View flyer
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
